import React from 'react';
import {
	AbsoluteFill,
	useCurrentFrame,
	useVideoConfig,
	spring,
	interpolate,
} from 'remotion';

/**
 * Procedural City Skyline - 程序化城市天际线
 *
 * 讲述"城市的一天"：从黎明到深夜的完整日夜交替
 *
 * 技术要点：
 * - 程序化建筑生成（随机高度、宽度、窗户）
 * - 三层建筑视差（远景、中景、近景）
 * - 日夜交替天空渐变
 * - 太阳/月亮轨迹运动
 * - 飘动的云朵、飞鸟、流星
 * - 窗户灯光随昼夜明灭
 * - 路灯与街道光效
 * - 叙事文字分阶段呈现
 */
export const ProceduralCitySkyline: React.FC = () => {
	const frame = useCurrentFrame();
	const { fps, durationInFrames } = useVideoConfig();

	const seededRandom = (seed: number) => {
		const x = Math.sin(seed) * 10000;
		return x - Math.floor(x);
	};

	// 日夜进度 0→1 (黎明→夜晚)
	const dayProgress = interpolate(frame, [0, durationInFrames], [0, 1], {
		extrapolateRight: 'clamp',
	});

	// ── 天空颜色系统 ──
	const skyTopHue = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [250, 210, 210, 280, 240]);
	const skyTopSat = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [70, 60, 50, 70, 80]);
	const skyTopLight = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [8, 55, 70, 20, 6]);

	const skyMidHue = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [280, 200, 200, 320, 260]);
	const skyMidSat = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [60, 50, 40, 80, 70]);
	const skyMidLight = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [15, 65, 78, 30, 10]);

	const skyBotHue = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [30, 40, 50, 20, 250]);
	const skyBotSat = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [80, 70, 40, 95, 60]);
	const skyBotLight = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [30, 75, 85, 55, 8]);

	const skyGradient = `linear-gradient(to bottom,
		hsl(${skyTopHue}, ${skyTopSat}%, ${skyTopLight}%) 0%,
		hsl(${skyMidHue}, ${skyMidSat}%, ${skyMidLight}%) 50%,
		hsl(${skyBotHue}, ${skyBotSat}%, ${skyBotLight}%) 100%
	)`;

	// ── 太阳 ──
	const sunAngle = interpolate(dayProgress, [0.05, 0.75], [-0.15, 1.15], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const sunX = interpolate(sunAngle, [0, 1], [200, 1720]);
	const sunY = 900 - Math.sin(sunAngle * Math.PI) * 700;
	const sunOpacity = interpolate(dayProgress, [0, 0.08, 0.7, 0.8], [0, 1, 1, 0], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const sunRadius = interpolate(dayProgress, [0.05, 0.3, 0.6, 0.75], [50, 40, 40, 55], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const sunColor = interpolate(dayProgress, [0.05, 0.25, 0.6, 0.75], [0, 1, 1, 0], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	// ── 月亮 ──
	const moonOpacity = interpolate(dayProgress, [0, 0.05, 0.8, 0.88], [0.7, 0, 0, 0.9], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const moonX = interpolate(dayProgress, [0.8, 1], [1500, 1350], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const moonY = interpolate(dayProgress, [0.8, 1], [250, 160], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	// ── 星星（黎明和夜晚可见） ──
	const starsOpacity = interpolate(dayProgress, [0, 0.12, 0.18, 0.78, 0.88, 1], [0.8, 0.3, 0, 0, 0.4, 0.9], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const stars = Array.from({ length: 120 }, (_, i) => ({
		x: seededRandom(i * 7) * 1920,
		y: seededRandom(i * 11) * 500,
		size: 0.8 + seededRandom(i * 13) * 2,
		twinkle: 0.5 + Math.sin(frame * 0.08 + i * 1.3) * 0.5,
	}));

	// ── 流星（夜晚阶段） ──
	const shootingStarProgress = interpolate(frame, [durationInFrames * 0.88, durationInFrames * 0.93], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const shootingStarX = interpolate(shootingStarProgress, [0, 1], [1600, 800]);
	const shootingStarY = interpolate(shootingStarProgress, [0, 1], [80, 350]);
	const shootingStarOpacity = interpolate(shootingStarProgress, [0, 0.1, 0.6, 1], [0, 1, 0.8, 0]);

	// ── 云朵 ──
	const clouds = [
		{ baseX: -200, y: 150, width: 280, height: 60, speed: 0.4, seed: 1 },
		{ baseX: 400, y: 100, width: 200, height: 45, speed: 0.3, seed: 2 },
		{ baseX: 900, y: 200, width: 320, height: 70, speed: 0.5, seed: 3 },
		{ baseX: 1400, y: 130, width: 240, height: 50, speed: 0.35, seed: 4 },
		{ baseX: 1700, y: 250, width: 180, height: 40, speed: 0.25, seed: 5 },
	];
	const cloudOpacity = interpolate(dayProgress, [0, 0.15, 0.7, 0.85, 1], [0.1, 0.6, 0.5, 0.15, 0.05], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	// ── 飞鸟（白天出现） ──
	const birdGroupOpacity = interpolate(dayProgress, [0.15, 0.25, 0.65, 0.75], [0, 1, 1, 0], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const birdOffsetX = frame * 2.5;
	const birdPairs = [
		{ baseX: 300, baseY: 280, phase: 0 },
		{ baseX: 340, baseY: 260, phase: 0.8 },
		{ baseX: 370, baseY: 290, phase: 1.6 },
		{ baseX: 310, baseY: 310, phase: 2.4 },
		{ baseX: 360, baseY: 240, phase: 3.2 },
	];

	// ── 建筑生成 ──
	const generateBuildings = (count: number, layer: number, seed: number) => {
		const buildings = [];
		let currentX = -50;
		for (let i = 0; i < count; i++) {
			const h = seededRandom(seed + i * 3) * 300 + 100;
			const w = seededRandom(seed + i * 7) * 80 + 40;
			const height = h * (1 - layer * 0.25);
			const width = w * (1 - layer * 0.15);
			const hasAntenna = seededRandom(seed + i * 17) > 0.7;
			const antennaHeight = seededRandom(seed + i * 19) * 40 + 20;
			buildings.push({
				x: currentX,
				height,
				width,
				windowCols: Math.floor(seededRandom(seed + i * 11) * 8) + 3,
				windowRows: Math.floor(height / 30),
				hasAntenna,
				antennaHeight,
			});
			currentX += width + seededRandom(seed + i * 13) * 10;
		}
		return buildings;
	};

	const layer0 = generateBuildings(28, 0, 12345);
	const layer1 = generateBuildings(20, 1, 67890);
	const layer2 = generateBuildings(14, 2, 11111);

	// 窗户亮度：白天低，夜晚高
	const windowBrightness = interpolate(dayProgress, [0, 0.15, 0.25, 0.7, 0.82, 1], [0.5, 0.15, 0.05, 0.05, 0.6, 0.95], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	const renderBuildings = (
		buildings: ReturnType<typeof generateBuildings>,
		baseY: number,
		bodyColor: string,
		litColor: string,
		delay: number,
		layerIdx: number,
	) =>
		buildings.map((b, i) => {
			const s = spring({
				frame: frame - delay - i * 2,
				fps,
				config: { damping: 15, stiffness: 80 },
			});
			const scale = interpolate(s, [0, 1], [0, 1]);
			const opacity = interpolate(s, [0, 1], [0, 1]);

			const windows: React.ReactElement[] = [];
			const wWidth = b.width / (b.windowCols + 1);

			for (let row = 0; row < b.windowRows; row++) {
				for (let col = 0; col < b.windowCols; col++) {
					const wx = b.x + wWidth * (col + 0.8);
					const wy = baseY - b.height + row * 25 + 10;
					const isLit = seededRandom(i * 100 + row * 10 + col + layerIdx * 1000) > 0.35;
					const flicker = 0.7 + Math.sin(frame * 0.04 + i * 0.7 + row * 0.3 + col * 0.5) * 0.3;
					const litAlpha = isLit ? windowBrightness * flicker : windowBrightness * 0.08;

					windows.push(
						<rect
							key={`${i}-${row}-${col}`}
							x={wx}
							y={wy}
							width={wWidth * 0.65}
							height={14}
							fill={isLit ? litColor : 'rgba(40,40,60,0.5)'}
							opacity={Math.max(0.03, litAlpha) * opacity}
							rx={1}
						/>,
					);
				}
			}

			return (
				<g key={i} style={{ transform: `scaleY(${scale})`, transformOrigin: `${b.x}px ${baseY}px` }}>
					<rect x={b.x} y={baseY - b.height} width={b.width} height={b.height} fill={bodyColor} opacity={opacity} />
					{b.hasAntenna && (
						<>
							<line
								x1={b.x + b.width / 2}
								y1={baseY - b.height - b.antennaHeight}
								x2={b.x + b.width / 2}
								y2={baseY - b.height}
								stroke={bodyColor}
								strokeWidth={2}
								opacity={opacity}
							/>
							<circle
								cx={b.x + b.width / 2}
								cy={baseY - b.height - b.antennaHeight}
								r={3}
								fill={dayProgress > 0.78 ? '#ff3333' : bodyColor}
								opacity={dayProgress > 0.78 ? 0.5 + Math.sin(frame * 0.3) * 0.5 : opacity}
							/>
						</>
					)}
					{windows}
				</g>
			);
		});

	// ── 路灯 ──
	const streetLampOpacity = interpolate(dayProgress, [0, 0.05, 0.15, 0.72, 0.82, 1], [0.6, 0.3, 0, 0, 0.7, 0.9], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const lamps = [160, 420, 680, 940, 1200, 1460, 1720];

	// ── 叙事文字 ──
	interface NarrativeItem {
		text: string;
		sub: string;
		startRatio: number;
		endRatio: number;
	}
	const narratives: NarrativeItem[] = [
		{ text: '黎明 · 初醒', sub: '城市还在沉睡，第一缕光悄然降临', startRatio: 0, endRatio: 0.16 },
		{ text: '晨光 · 启程', sub: '朝阳升起，万物苏醒，新的一天开始了', startRatio: 0.18, endRatio: 0.36 },
		{ text: '正午 · 繁忙', sub: '车水马龙，人潮涌动，这座城市生机勃勃', startRatio: 0.38, endRatio: 0.56 },
		{ text: '黄昏 · 归途', sub: '夕阳西沉，倦鸟归巢，城市染上温暖的金色', startRatio: 0.6, endRatio: 0.78 },
		{ text: '夜幕 · 万家灯火', sub: '星河璀璨，灯火阑珊，每扇窗后都是一个故事', startRatio: 0.82, endRatio: 1 },
	];

	const activeNarrative = narratives.find((n) => dayProgress >= n.startRatio && dayProgress <= n.endRatio);
	let narrativeOpacity = 0;
	if (activeNarrative) {
		const mid = (activeNarrative.startRatio + activeNarrative.endRatio) / 2;
		const half = (activeNarrative.endRatio - activeNarrative.startRatio) / 2;
		narrativeOpacity = interpolate(
			dayProgress,
			[activeNarrative.startRatio, activeNarrative.startRatio + half * 0.3, mid, activeNarrative.endRatio - half * 0.3, activeNarrative.endRatio],
			[0, 1, 1, 1, 0],
			{ extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
		);
	}

	// ── 地面反光强度 ──
	const groundGlowOpacity = interpolate(dayProgress, [0, 0.1, 0.2, 0.7, 0.8, 1], [0.2, 0.4, 0.1, 0.1, 0.3, 0.5], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const groundHue = interpolate(dayProgress, [0, 0.2, 0.5, 0.75, 1], [30, 40, 50, 20, 240]);

	// ── 进度条 ──
	const progressBarWidth = interpolate(dayProgress, [0, 1], [0, 1600]);
	const progressBarOpacity = interpolate(
		frame,
		[0, 20, durationInFrames - 20, durationInFrames],
		[0, 0.5, 0.5, 0],
		{ extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
	);

	// ── 光晕效果（太阳附近） ──
	const sunGlowRadius = sunRadius * 3;
	const sunGlowOpacity = sunOpacity * 0.3;

	return (
		<AbsoluteFill style={{ background: skyGradient }}>
			<svg width={1920} height={1080} viewBox="0 0 1920 1080">
				<defs>
					<radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
						<stop offset="0%" stopColor={sunColor > 0.5 ? '#fff8e0' : '#ffaa44'} stopOpacity={0.6} />
						<stop offset="100%" stopColor={sunColor > 0.5 ? '#fff8e0' : '#ff6622'} stopOpacity={0} />
					</radialGradient>
					<radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
						<stop offset="0%" stopColor="#c8d8ff" stopOpacity={0.4} />
						<stop offset="100%" stopColor="#c8d8ff" stopOpacity={0} />
					</radialGradient>
					<radialGradient id="lampGlow" cx="50%" cy="0%" r="80%">
						<stop offset="0%" stopColor="#ffdd88" stopOpacity={0.7} />
						<stop offset="100%" stopColor="#ffdd88" stopOpacity={0} />
					</radialGradient>
					<linearGradient id="shootingStar" x1="0%" y1="0%" x2="100%" y2="0%">
						<stop offset="0%" stopColor="white" stopOpacity={0} />
						<stop offset="80%" stopColor="white" stopOpacity={0.8} />
						<stop offset="100%" stopColor="white" stopOpacity={1} />
					</linearGradient>
				</defs>

				{/* 星空 */}
				<g opacity={starsOpacity}>
					{stars.map((star, i) => (
						<circle key={i} cx={star.x} cy={star.y} r={star.size} fill="white" opacity={star.twinkle} />
					))}
				</g>

				{/* 太阳光晕 */}
				<circle cx={sunX} cy={sunY} r={sunGlowRadius} fill="url(#sunGlow)" opacity={sunGlowOpacity} />
				{/* 太阳 */}
				<circle
					cx={sunX}
					cy={sunY}
					r={sunRadius}
					fill={sunColor > 0.5 ? '#fff4cc' : '#ff8833'}
					opacity={sunOpacity}
				/>

				{/* 月亮 */}
				<g opacity={moonOpacity}>
					<circle cx={moonX} cy={moonY} r={70} fill="url(#moonGlow)" />
					<circle cx={moonX} cy={moonY} r={28} fill="#e8eeff" />
					<circle cx={moonX + 10} cy={moonY - 2} r={26} fill={`hsl(${skyTopHue}, ${skyTopSat}%, ${skyTopLight}%)`} />
				</g>

				{/* 流星 */}
				<line
					x1={shootingStarX}
					y1={shootingStarY}
					x2={shootingStarX + 80}
					y2={shootingStarY - 30}
					stroke="url(#shootingStar)"
					strokeWidth={2}
					opacity={shootingStarOpacity}
				/>

				{/* 云朵 */}
				<g opacity={cloudOpacity}>
					{clouds.map((c, ci) => {
						const cx = ((c.baseX + frame * c.speed * 30) % (1920 + c.width * 2)) - c.width;
						return (
							<g key={ci}>
								<ellipse cx={cx + c.width * 0.3} cy={c.y} rx={c.width * 0.35} ry={c.height * 0.8} fill="white" opacity={0.7} />
								<ellipse cx={cx + c.width * 0.55} cy={c.y - c.height * 0.2} rx={c.width * 0.4} ry={c.height} fill="white" opacity={0.8} />
								<ellipse cx={cx + c.width * 0.8} cy={c.y} rx={c.width * 0.3} ry={c.height * 0.7} fill="white" opacity={0.6} />
							</g>
						);
					})}
				</g>

				{/* 飞鸟 */}
				<g opacity={birdGroupOpacity}>
					{birdPairs.map((bird, bi) => {
						const bx = ((bird.baseX + birdOffsetX) % 1920) - 50;
						const by = bird.baseY + Math.sin(frame * 0.15 + bird.phase) * 8;
						const wingAngle = Math.sin(frame * 0.4 + bird.phase) * 15;
						return (
							<g key={bi} transform={`translate(${bx}, ${by})`}>
								<line x1={0} y1={0} x2={-10} y2={-wingAngle * 0.5} stroke="#333" strokeWidth={1.5} strokeLinecap="round" />
								<line x1={0} y1={0} x2={10} y2={-wingAngle * 0.5} stroke="#333" strokeWidth={1.5} strokeLinecap="round" />
							</g>
						);
					})}
				</g>

				{/* 远景建筑 */}
				{renderBuildings(layer0, 1080, 'rgba(30,30,50,0.5)', 'rgba(255,220,150,0.9)', 10, 0)}

				{/* 中景建筑 */}
				{renderBuildings(layer1, 1080, 'rgba(40,40,65,0.75)', 'rgba(255,200,100,0.9)', 25, 1)}

				{/* 近景建筑 */}
				{renderBuildings(layer2, 1080, 'rgba(50,50,75,1)', 'rgba(255,180,80,1)', 40, 2)}

				{/* 路灯 */}
				<g opacity={streetLampOpacity}>
					{lamps.map((lx, li) => (
						<g key={li}>
							<line x1={lx} y1={1080} x2={lx} y2={980} stroke="#556" strokeWidth={3} />
							<ellipse cx={lx} cy={978} rx={6} ry={4} fill="#ffdd88" />
							<ellipse cx={lx} cy={1010} rx={35} ry={50} fill="url(#lampGlow)" opacity={0.5} />
						</g>
					))}
				</g>

				{/* 地面反光 */}
				<rect x={0} y={1065} width={1920} height={15} fill={`hsla(${groundHue}, 80%, 50%, 0.4)`} opacity={groundGlowOpacity} rx={2} />

				{/* 叙事文字 */}
				{activeNarrative && (
					<g opacity={narrativeOpacity}>
						<text
							x={960}
							y={480}
							textAnchor="middle"
							fill="white"
							fontSize={64}
							fontFamily="'PingFang SC', 'Microsoft YaHei', sans-serif"
							fontWeight="bold"
							style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
						>
							{activeNarrative.text}
						</text>
						<text
							x={960}
							y={540}
							textAnchor="middle"
							fill="rgba(255,255,255,0.8)"
							fontSize={30}
							fontFamily="'PingFang SC', 'Microsoft YaHei', sans-serif"
						>
							{activeNarrative.sub}
						</text>
					</g>
				)}

				{/* 底部时间进度条 */}
				<rect x={160} y={1050} width={1600} height={3} fill="rgba(255,255,255,0.15)" rx={1.5} opacity={progressBarOpacity} />
				<rect x={160} y={1050} width={progressBarWidth} height={3} fill="rgba(255,255,255,0.6)" rx={1.5} opacity={progressBarOpacity} />

				{/* 标题 */}
				<text
					x={960}
					y={1040}
					textAnchor="middle"
					fill="rgba(255,255,255,0.35)"
					fontSize={24}
					fontFamily="'PingFang SC', 'Microsoft YaHei', sans-serif"
					opacity={interpolate(
						spring({ frame: frame - 50, fps, config: { damping: 20 } }),
						[0, 1],
						[0, 1],
					)}
				>
					#412 Procedural City Skyline · 城市的一天
				</text>
			</svg>
		</AbsoluteFill>
	);
};
